import * as React from "react";
import {
  Drawer,
  Box,
  Typography,
  Paper,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Collapse,
} from "@mui/material";

import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { fludeeNavGroups } from "../partail/fludeeNav";
import { ROUTES } from "../shared/constants/routes";

const leftDrawerWidth = 280;

function isGroupActive(group, currentPath) {
  return group.children?.some((child) => child.path === currentPath);
}

export default function LeftDrawer({ open, onClose, currentPath, onNavigate }) {
  const [expanded, setExpanded] = React.useState(() => ({
    "fludee-device": currentPath.startsWith("/monitoring"),
    monitoring: currentPath.startsWith("/monitoring"),
    vitals: currentPath.startsWith("/vitals"),
    balance: currentPath.startsWith("/balance"),
    medicine: currentPath.startsWith("/medicine"),
  }));

  React.useEffect(() => {
    setExpanded((prev) => ({
      ...prev,
      "fludee-device": prev["fludee-device"] || currentPath.startsWith("/monitoring"),
      monitoring: prev.monitoring || currentPath.startsWith("/monitoring"),
      vitals: prev.vitals || currentPath.startsWith("/vitals"),
      balance: prev.balance || currentPath.startsWith("/balance"),
      medicine: prev.medicine || currentPath.startsWith("/medicine"),
    }));
  }, [currentPath]);

  const toggleGroup = (key) => {
    setExpanded((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      PaperProps={{
        sx: {
          width: { xs: "86vw", sm: leftDrawerWidth },
          maxWidth: leftDrawerWidth,
          borderTopRightRadius: 16,
          borderBottomRightRadius: 16,
        },
      }}
    >
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          px: 2.5,
          py: 2,
          bgcolor: "#ffffff",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: 2.5,
              bgcolor: "#0b3c3f",
              display: "grid",
              placeItems: "center",
              color: "#fff",
              fontWeight: 900,
              userSelect: "none",
            }}
          >
            F
          </Box>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#0b3c3f" }}>
            Fludee
          </Typography>
        </Box>

        <Paper
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 2,
            bgcolor: "#0b3c3f",
            color: "#fff",
            px: 2,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            boxShadow: "0 10px 22px rgba(11,60,63,0.18)",
          }}
        >
          <DashboardOutlinedIcon />
          <Typography sx={{ fontWeight: 700 }}>Fludee Dashboard</Typography>
        </Paper>

        <List
          sx={{
            px: 0,
            py: 0,
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
          }}
        >
          {fludeeNavGroups.map((node) => {
            const hasChildren = Array.isArray(node.children) && node.children.length > 0;

            if (!hasChildren) {
              const selected = node.path === currentPath;

              return (
                <ListItemButton
                  key={node.key}
                  onClick={() => onNavigate(node.path)}
                  sx={{
                    borderRadius: 2,
                    px: 1.5,
                    py: 1.1,
                    color: selected ? "#0b3c3f" : "#2f4f51",
                    bgcolor: selected ? "#edf5f5" : "transparent",
                    "&:hover": { bgcolor: "#f4f7f9" },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 40,
                      color: selected ? "#0b3c3f" : "#5a7b7d",
                    }}
                  >
                    {node.icon}
                  </ListItemIcon>

                  <ListItemText
                    primary={node.label}
                    primaryTypographyProps={{
                      fontSize: 15,
                      fontWeight: selected ? 800 : 600,
                    }}
                  />
                </ListItemButton>
              );
            }

            const groupSelected = isGroupActive(node, currentPath);
            const isOpen = !!expanded[node.key];

            return (
              <Box key={node.key}>
                <ListItemButton
                  onClick={() => toggleGroup(node.key)}
                  sx={{
                    borderRadius: 2,
                    px: 1.5,
                    py: 1.1,
                    color: groupSelected ? "#0b3c3f" : "#2f4f51",
                    bgcolor: groupSelected ? "#edf5f5" : "transparent",
                    "&:hover": { bgcolor: "#f4f7f9" },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 40,
                      color: groupSelected ? "#0b3c3f" : "#5a7b7d",
                    }}
                  >
                    {node.icon}
                  </ListItemIcon>

                  <ListItemText
                    primary={node.label}
                    primaryTypographyProps={{
                      fontSize: 15,
                      fontWeight: groupSelected ? 800 : 700,
                    }}
                  />

                  {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </ListItemButton>

                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <List
                    sx={{
                      pl: 1.25,
                      pt: 0.5,
                      pb: 0.5,
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.25,
                    }}
                  >
                    {node.children.map((child) => {
                      const selected = child.path === currentPath;

                      return (
                        <ListItemButton
                          key={child.key}
                          onClick={() => onNavigate(child.path)}
                          sx={{
                            borderRadius: 2,
                            px: 1.25,
                            py: 0.9,
                            ml: 0.5,
                            color: selected ? "#0b3c3f" : "#2f4f51",
                            bgcolor: selected ? "#edf5f5" : "transparent",
                            "&:hover": { bgcolor: "#f4f7f9" },
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 36,
                              color: selected ? "#0b3c3f" : "#5a7b7d",
                            }}
                          >
                            {child.icon}
                          </ListItemIcon>

                          <ListItemText
                            primary={child.label}
                            primaryTypographyProps={{
                              fontSize: 14,
                              fontWeight: selected ? 800 : 600,
                            }}
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Collapse>
              </Box>
            );
          })}
        </List>

        <Box sx={{ flex: 1 }} />
        <Divider sx={{ my: 2 }} />

        <List sx={{ px: 0, py: 0 }}>
          <ListItemButton
            onClick={() => onNavigate(ROUTES.SETTINGS)}
            sx={{
              borderRadius: 2,
              px: 1.5,
              py: 1.1,
              "&:hover": { bgcolor: "#f4f7f9" },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40, color: "#5a7b7d" }}>
              <SettingsOutlinedIcon />
            </ListItemIcon>
            <ListItemText
              primary="Settings"
              primaryTypographyProps={{ fontSize: 15, fontWeight: 700 }}
            />
          </ListItemButton>
        </List>
      </Box>
    </Drawer>
  );
}